import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Trash, UserPlus, UserCircleGear, Link as LinkIcon, Copy, ArrowsClockwise, ShareNetwork, Users, CalendarCheck, ChartBar } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { useAuth } from "../../lib/auth";
import LeadersBulkDialog from "./LeadersBulkDialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

export default function AdminAgents() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isMP = user?.role === "managing_partner";
  const [users, setUsers] = useState([]);
  const [mps, setMps] = useState([]);
  const [open, setOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null); // agent being reassigned
  const [invite, setInvite] = useState(null);
  const [inviteStats, setInviteStats] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent", managing_partner_id: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [adminInviteOpen, setAdminInviteOpen] = useState(false);
  const [adminInvites, setAdminInvites] = useState([]);
  const [adminInviteForm, setAdminInviteForm] = useState({ role: "agent", managing_partner_id: "", field_director_id: "", note: "" });
  const [adminInviteCreating, setAdminInviteCreating] = useState(false);
  const [latestAdminInvite, setLatestAdminInvite] = useState(null);

  const load = async () => {
    const [u, m] = await Promise.all([
      api.get("/users"),
      api.get("/managing-partners"),
    ]);
    setUsers(u.data);
    setMps(m.data);
  };
  useEffect(() => { load(); }, []);

  const loadInvite = async () => {
    const [inv, st] = await Promise.all([
      api.get("/my-invite"),
      api.get("/my-invite/stats"),
    ]);
    setInvite(inv.data);
    setInviteStats(st.data);
  };
  useEffect(() => {
    if (isMP) loadInvite();
  }, [isMP]);

  const loadAdminInvites = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/admin/invites");
      setAdminInvites(data?.rows || []);
    } catch {
      setAdminInvites([]);
    }
  }, [isAdmin]);

  useEffect(() => { loadAdminInvites(); }, [loadAdminInvites]);

  const createAdminInvite = async (e) => {
    e.preventDefault();
    setAdminInviteCreating(true);
    try {
      const payload = {
        role: adminInviteForm.role,
        managing_partner_id: null,
        field_director_id: null,
        note: adminInviteForm.note?.trim() || null,
      };
      const { data } = await api.post("/admin/invites", payload);
      const url = `${window.location.origin}${data.path}`;
      setLatestAdminInvite({ ...data, url });
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Universal invite created & copied");
      } catch {
        toast.success("Universal invite created");
      }
      setAdminInviteForm({ role: "agent", managing_partner_id: "", field_director_id: "", note: "" });
      loadAdminInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create invite");
    } finally {
      setAdminInviteCreating(false);
    }
  };

  const copyAdminInvite = async (code) => {
    const url = `${window.location.origin}/join/${code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const revokeAdminInvite = async (code) => {
    if (!window.confirm("Revoke this invite? The link will stop working immediately.")) return;
    try {
      await api.post(`/admin/invites/${code}/revoke`);
      toast.success("Invite revoked");
      loadAdminInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not revoke");
    }
  };

  const inviteUrl = invite ? `${window.location.origin}/join/${invite.invite_code}` : "";

  const copyInvite = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const regenerateInvite = async () => {
    if (!window.confirm("Rotate your invite link? The old link will stop working immediately.")) return;
    const { data } = await api.post("/my-invite/regenerate");
    setInvite(data);
    toast.success("New invite link generated");
  };

  const shareInvite = async () => {
    if (!inviteUrl) return;
    const text = `Join my CFN team — sign up here: ${inviteUrl}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Join CFN", text, url: inviteUrl });
      } catch (e) {
        // User cancelled the native share sheet or share API failed —
        // either case is a no-op. Don't fall back to clipboard here because
        // a cancellation is not an error worth toasting.
        if (e?.name !== "AbortError") {
          console.warn("Native share failed:", e?.message || e);
        }
      }
    } else {
      await navigator.clipboard.writeText(text);
      toast.success("Share message copied");
    }
  };

  const openAdd = () => {
    setForm({
      name: "", email: "", password: "",
      role: "agent",
      managing_partner_id: isMP ? user.id : "",
      field_director_id: "",
    });
    setOpen(true);
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.managing_partner_id) delete payload.managing_partner_id;
      if (!payload.field_director_id) delete payload.field_director_id;
      if (isMP && !["agent", "field_director"].includes(payload.role)) payload.role = "agent";
      await api.post("/auth/register", payload);
      const labels = { admin: "Admin", managing_partner: "Managing Partner", field_director: "Field Director", agent: "Agent" };
      toast.success(`${labels[payload.role] || "User"} added`);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create user");
    }
  };

  const fieldDirectors = users.filter((u) => u.role === "field_director");

  const toggleRole = async (u) => {
    const next = u.role === "field_director" ? "agent" : "field_director";
    const verb = next === "field_director" ? "Promote" : "Demote";
    if (!window.confirm(`${verb} ${u.name} to ${next === "field_director" ? "Field Director" : "Agent"}?`)) return;
    try {
      await api.patch(`/users/${u.id}/role`, { role: next });
      toast.success(`${verb}d`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const reassignFd = async (agentId, newFdId) => {
    try {
      await api.patch(`/users/${agentId}/assign-fd`, { field_director_id: newFdId || null });
      toast.success(newFdId ? "Assigned to Field Director" : "Unassigned from Field Director");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This removes their appointments & activity.`)) return;
    await api.delete(`/users/${u.id}`);
    toast.success("Removed");
    load();
  };

  const reassign = async (agent, newMpId) => {
    try {
      await api.patch(`/users/${agent.id}/assign-mp`, { managing_partner_id: newMpId || null });
      toast.success(newMpId ? "Assigned" : "Unassigned");
      setAssignOpen(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitBulkGdc = async (e) => {
    e.preventDefault();
    setBulkSubmitting(true);
    try {
      // Parse CSV. New format: first_name,last_name,period,product_line,amount[,notes]
      // Legacy fallback: agent_email,period,product_line,amount[,notes]
      const lines = bulkCsv.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      const entries = [];
      const errors = [];
      const looksLikeEmail = (s) => s.includes("@");
      const looksLikeUuid = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
      lines.forEach((line, idx) => {
        // Skip header row
        if (idx === 0 && /(first.*last|agent.*period|email.*period)/i.test(line)) return;
        const parts = line.split(",").map((p) => p.trim());
        if (parts.length < 4) { errors.push(`Row ${idx + 1}: need at least 4 columns`); return; }
        const useLegacy = looksLikeEmail(parts[0]) || looksLikeUuid(parts[0]);
        if (useLegacy) {
          if (parts.length < 4) { errors.push(`Row ${idx + 1}: legacy format needs email,period,product_line,amount`); return; }
          const [agent_email_or_id, period, product_line, amountStr, ...rest] = parts;
          const amount = parseFloat(amountStr);
          if (isNaN(amount)) { errors.push(`Row ${idx + 1}: invalid amount`); return; }
          entries.push({ agent_email_or_id, period, product_line, amount, notes: rest.join(",") || null });
        } else {
          if (parts.length < 5) { errors.push(`Row ${idx + 1}: need first_name,last_name,period,product_line,amount`); return; }
          const [first_name, last_name, period, product_line, amountStr, ...rest] = parts;
          const amount = parseFloat(amountStr);
          if (isNaN(amount)) { errors.push(`Row ${idx + 1}: invalid amount`); return; }
          entries.push({ first_name, last_name, period, product_line, amount, notes: rest.join(",") || null });
        }
      });
      if (errors.length) {
        toast.error(errors.slice(0, 3).join(" · "));
        return;
      }
      if (!entries.length) { toast.error("No valid rows to import"); return; }
      const { data } = await api.post("/awards/gdc/bulk", { entries });
      setBulkResult(data);
      toast.success(`${data.inserted} entries imported${data.skipped?.length ? `, ${data.skipped.length} skipped` : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const mpName = (id) => mps.find((m) => m.id === id)?.name || "—";
  const visibleUsers = users.filter((u) => {
    if (isAdmin) return true;
    // MP sees self + own agents (plus other MPs in the firm)
    if (u.id === user.id) return true;
    if (u.role === "agent" && u.managing_partner_id === user.id) return true;
    return false;
  });

  const title = isAdmin ? "Agents, MPs & Admins" : "My Team";
  const subtitle = isAdmin ? "Manage your roster." : "Your downline agents.";

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{isAdmin ? "Admin" : "Managing Partner"}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1">{title}</h1>
          <p className="text-zinc-600 mt-2">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin && <LeadersBulkDialog />}
          {isAdmin && (
            <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkResult(null); setBulkCsv(""); } }}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-white text-black border-2 border-black hover:bg-[#e9d5ff] shadow-[4px_4px_0_0_#09090b] font-bold uppercase tracking-widest text-xs h-11" data-testid="bulk-gdc-btn">
                  Bulk GDC
                </Button>
              </DialogTrigger>
              <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b] max-w-2xl">
                <DialogHeader><DialogTitle className="font-display font-black tracking-tight">Bulk Import GDC</DialogTitle></DialogHeader>
                <form onSubmit={submitBulkGdc} className="space-y-3">
                  <p className="text-sm text-zinc-600">
                    Paste CSV rows (one per line). Format: <code className="bg-zinc-100 px-1">first_name,last_name,period,product_line,amount[,notes]</code>. Period is YYYY-MM. Product line: Life | Disability | LTC | Annuity | Investment | Osaic. (Email is still accepted as the first column for legacy use.)
                  </p>
                  <Textarea
                    rows={10}
                    value={bulkCsv}
                    onChange={(e) => setBulkCsv(e.target.value)}
                    placeholder={"Sarah,Connor,2026-01,Life,4500\nJohn,Smith,2026-01,Annuity,2200\nMaria,Garcia,2026-02,Osaic,9800"}
                    className="border-2 border-black rounded-none focus-visible:ring-0 font-mono text-sm"
                    data-testid="bulk-csv-input"
                  />
                  {bulkResult && (
                    <div className="border-2 border-black p-3 bg-zinc-50 text-sm" data-testid="bulk-result">
                      <div className="font-bold">{bulkResult.inserted} inserted · {bulkResult.skipped?.length || 0} skipped</div>
                      {bulkResult.skipped?.length > 0 && (
                        <ul className="mt-2 list-disc list-inside text-zinc-600">
                          {bulkResult.skipped.slice(0, 5).map((s, i) => {
                            const r = s.row;
                            const who = r.first_name || r.last_name ? `${r.first_name || ""} ${r.last_name || ""}`.trim() : r.agent_email_or_id;
                            return <li key={`${who}-${s.reason}-${i}`}>{who} · {s.reason}</li>;
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={bulkSubmitting || !bulkCsv.trim()}
                    className="w-full rounded-none bg-[#09090b] text-white border-2 border-black h-11 font-bold uppercase tracking-widest text-xs"
                    data-testid="bulk-submit-btn"
                  >
                    {bulkSubmitting ? "Importing…" : "Import"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAdd} className="rounded-none bg-[#09090b] text-white border-2 border-black hover:bg-black shadow-[4px_4px_0_0_#2563eb] font-bold uppercase tracking-widest text-xs h-11" data-testid="add-agent-btn">
              <UserPlus size={16} weight="bold" className="mr-1" /> {isMP ? "Add Agent" : "Add User"}
            </Button>
          </DialogTrigger>
          <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b]">
            <DialogHeader><DialogTitle className="font-display font-black tracking-tight">{isMP ? "Add Agent to Team" : "Add User"}</DialogTitle></DialogHeader>
            <form onSubmit={add} className="space-y-4">
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0" data-testid="agent-name" />
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0" data-testid="agent-email" />
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Password</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0" data-testid="agent-password" />
              </div>
              {isAdmin && (
                <>
                  <div>
                    <Label className="font-bold uppercase text-xs tracking-widest">Role</Label>
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, managing_partner_id: ["agent", "field_director"].includes(e.target.value) ? form.managing_partner_id : "", field_director_id: "" })}
                      className="mt-1 w-full h-11 border-2 border-black px-3 bg-white font-medium" data-testid="agent-role">
                      <option value="agent">Agent</option>
                      <option value="field_director">Field Director</option>
                      <option value="managing_partner">Managing Partner</option>
                      {isAdmin && <option value="admin">Admin</option>}
                    </select>
                  </div>
                  {(form.role === "agent" || form.role === "field_director") && (
                    <div>
                      <Label className="font-bold uppercase text-xs tracking-widest">Managing Partner (optional)</Label>
                      <select value={form.managing_partner_id} onChange={(e) => setForm({ ...form, managing_partner_id: e.target.value })}
                        className="mt-1 w-full h-11 border-2 border-black px-3 bg-white font-medium" data-testid="agent-mp">
                        <option value="">— None —</option>
                        {mps.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </div>
                  )}
                  {form.role === "agent" && fieldDirectors.length > 0 && (
                    <div>
                      <Label className="font-bold uppercase text-xs tracking-widest">Field Director (optional)</Label>
                      <select value={form.field_director_id} onChange={(e) => setForm({ ...form, field_director_id: e.target.value })}
                        className="mt-1 w-full h-11 border-2 border-black px-3 bg-white font-medium" data-testid="agent-fd">
                        <option value="">— None —</option>
                        {fieldDirectors.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    </div>
                  )}
                </>
              )}
              <Button type="submit" className="w-full h-11 rounded-none bg-[#2563eb] text-white border-2 border-black shadow-[4px_4px_0_0_#09090b] hover:shadow-[6px_6px_0_0_#09090b] hover:-translate-y-0.5 font-bold uppercase tracking-widest" data-testid="save-agent-btn">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {isAdmin && (
        <div className="brut bg-white p-5" data-testid="admin-invite-section">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="border-2 border-black p-2 bg-[#c084fc] text-white">
                <LinkIcon size={20} weight="bold" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Admin Invites</div>
                <div className="font-display font-black text-2xl tracking-tight">Universal Sign-Up Links</div>
                <div className="text-sm text-zinc-600 mt-1">Reusable invite links — unlimited signups, never expire. Assign MP / Field Director from the roster table after the agent registers.</div>
              </div>
            </div>
            <Dialog open={adminInviteOpen} onOpenChange={(o) => { setAdminInviteOpen(o); if (!o) setLatestAdminInvite(null); }}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-none bg-[#09090b] text-white border-2 border-black hover:bg-black shadow-[4px_4px_0_0_#c084fc] font-bold uppercase tracking-widest text-xs h-11"
                  data-testid="open-admin-invite-btn"
                >
                  <LinkIcon size={14} weight="bold" className="mr-1" /> Create Universal Link
                </Button>
              </DialogTrigger>
              <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b]">
                <DialogHeader><DialogTitle className="font-display font-black tracking-tight">New Universal Invite</DialogTitle></DialogHeader>
                <form onSubmit={createAdminInvite} className="space-y-4">
                  <p className="text-sm text-zinc-600">
                    Anyone with this link can register. The link never expires and can be reused as many times as you like. New signups land with no MP / Field Director assigned — assign them after they appear in the roster below.
                  </p>
                  <div>
                    <Label className="font-bold uppercase text-xs tracking-widest">Role on Signup</Label>
                    <select
                      value={adminInviteForm.role}
                      onChange={(e) => setAdminInviteForm((f) => ({ ...f, role: e.target.value }))}
                      className="mt-1 w-full h-11 border-2 border-black px-3 bg-white font-medium"
                      data-testid="admin-invite-role"
                    >
                      <option value="agent">Agent</option>
                      <option value="field_director">Field Director</option>
                    </select>
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-xs tracking-widest">Note / Label (optional)</Label>
                    <Input
                      value={adminInviteForm.note}
                      onChange={(e) => setAdminInviteForm((f) => ({ ...f, note: e.target.value }))}
                      placeholder="e.g. Job-board campaign · Q1 2026"
                      className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0"
                      data-testid="admin-invite-note"
                    />
                  </div>
                  {latestAdminInvite && (
                    <div className="border-2 border-black p-3 bg-[#dcfce7]" data-testid="admin-invite-result">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-1">Your Universal Link</div>
                      <div className="flex items-center gap-2">
                        <input
                          readOnly
                          value={latestAdminInvite.url}
                          onFocus={(e) => e.target.select()}
                          className="flex-1 h-9 border-2 border-black px-2 font-mono text-xs bg-white focus:outline-none"
                          data-testid="admin-invite-result-url"
                        />
                        <Button type="button" onClick={() => copyAdminInvite(latestAdminInvite.code)} className="rounded-none h-9 px-2 bg-[#2563eb] text-white border-2 border-black" data-testid="admin-invite-result-copy">
                          <Copy size={14} weight="bold" />
                        </Button>
                      </div>
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={adminInviteCreating}
                    className="w-full h-11 rounded-none bg-[#c084fc] text-white border-2 border-black shadow-[4px_4px_0_0_#09090b] hover:shadow-[6px_6px_0_0_#09090b] hover:-translate-y-0.5 font-bold uppercase tracking-widest"
                    data-testid="admin-invite-submit-btn"
                  >
                    {adminInviteCreating ? "Generating…" : "Generate Universal Link"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {adminInvites.length > 0 && (
            <div className="mt-5 border-2 border-black overflow-x-auto" data-testid="admin-invites-table">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#09090b] text-white">
                    <th className="text-left px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Status</th>
                    <th className="text-left px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Role</th>
                    <th className="text-right px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Uses</th>
                    <th className="text-left px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Note</th>
                    <th className="text-left px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Created</th>
                    <th className="text-right px-3 py-2 font-bold uppercase text-[10px] tracking-widest">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adminInvites.slice(0, 25).map((inv) => (
                    <tr key={inv.code} className="border-b-2 border-black last:border-b-0" data-testid={`admin-invite-row-${inv.code}`}>
                      <td className="px-3 py-2">
                        <span
                          className="inline-block border-2 border-black px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                          style={{
                            background: inv.status === "active" ? "#22c55e" : "#fb7185",
                            color: "#fff",
                          }}
                        >
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-3 py-2">{inv.role === "field_director" ? "Field Director" : "Agent"}</td>
                      <td className="px-3 py-2 text-right font-display font-black">{inv.use_count || 0}</td>
                      <td className="px-3 py-2 text-zinc-700 max-w-[220px] truncate" title={inv.note || ""}>{inv.note || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-2 text-zinc-500 font-mono text-xs">{(inv.created_at || "").slice(0, 10)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => copyAdminInvite(inv.code)}
                            className="border-2 border-black p-1.5 hover:bg-[#2563eb] hover:text-white"
                            data-testid={`copy-admin-invite-${inv.code}`}
                            title="Copy link"
                          >
                            <Copy size={12} weight="bold" />
                          </button>
                          {inv.status === "active" && (
                            <button
                              onClick={() => revokeAdminInvite(inv.code)}
                              className="border-2 border-black p-1.5 hover:bg-[#fb7185] hover:text-white"
                              data-testid={`revoke-admin-invite-${inv.code}`}
                              title="Revoke"
                            >
                              <Trash size={12} weight="bold" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {isMP && (
        <div className="brut bg-white p-5" data-testid="recruiting-section">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="border-2 border-black p-2 bg-[#2563eb]">
                <LinkIcon size={20} weight="bold" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Recruiting</div>
                <div className="font-display font-black text-2xl tracking-tight">Share Your Invite Link</div>
                <div className="text-sm text-zinc-600 mt-1">Anyone who signs up through your link joins your team automatically.</div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-col md:flex-row gap-2">
            <input
              readOnly
              value={inviteUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 h-11 border-2 border-black px-3 font-mono text-sm bg-[#f4f4f5] focus:outline-none"
              data-testid="invite-link-input"
            />
            <Button onClick={copyInvite} disabled={!inviteUrl}
              className="rounded-none bg-[#2563eb] text-white border-2 border-black hover:bg-[#1d4ed8] shadow-[3px_3px_0_0_#09090b] active:shadow-none active:translate-x-1 active:translate-y-1 font-bold uppercase tracking-widest text-xs h-11"
              data-testid="copy-invite-btn">
              <Copy size={14} weight="bold" className="mr-1" /> Copy
            </Button>
            <Button onClick={shareInvite} disabled={!inviteUrl}
              className="rounded-none bg-[#09090b] text-white border-2 border-black hover:bg-black shadow-[3px_3px_0_0_#2563eb] active:shadow-none active:translate-x-1 active:translate-y-1 font-bold uppercase tracking-widest text-xs h-11"
              data-testid="share-invite-btn">
              <ShareNetwork size={14} weight="bold" className="mr-1" /> Share
            </Button>
            <Button onClick={regenerateInvite}
              className="rounded-none bg-white text-black border-2 border-black hover:bg-[#fb7185] hover:text-white shadow-[3px_3px_0_0_#09090b] active:shadow-none active:translate-x-1 active:translate-y-1 font-bold uppercase tracking-widest text-xs h-11"
              data-testid="regenerate-invite-btn">
              <ArrowsClockwise size={14} weight="bold" className="mr-1" /> Rotate
            </Button>
          </div>
          <div className="mt-3 text-xs text-zinc-500">
            New signups are auto-assigned to your downline. Rotating the link invalidates the old URL instantly.
          </div>

          {/* Recruiting analytics */}
          <div className="mt-6 pt-5 border-t-2 border-dashed border-zinc-300" data-testid="recruiting-analytics">
            <div className="flex items-center gap-2 mb-4">
              <ChartBar size={18} weight="bold" />
              <div className="font-display font-black text-xl tracking-tight">Recruiting Analytics</div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="border-2 border-black p-3 bg-[#f4f4f5]" data-testid="stat-total-signups">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  <Users size={14} weight="bold" /> Total
                </div>
                <div className="font-display font-black text-3xl tracking-tighter mt-1">{inviteStats?.total_signups ?? 0}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest">via invite link</div>
              </div>
              <div className="border-2 border-black p-3 bg-[#dbeafe]" data-testid="stat-month-signups">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  <CalendarCheck size={14} weight="bold" /> This Month
                </div>
                <div className="font-display font-black text-3xl tracking-tighter mt-1">{inviteStats?.this_month ?? 0}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest">new agents</div>
              </div>
              <div className="border-2 border-black p-3 bg-[#dcfce7]" data-testid="stat-week-signups">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  <CalendarCheck size={14} weight="bold" /> This Week
                </div>
                <div className="font-display font-black text-3xl tracking-tighter mt-1">{inviteStats?.this_week ?? 0}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest">fresh joins</div>
              </div>
            </div>

            {/* Monthly bar chart */}
            <div className="mt-4 border-2 border-black p-3 bg-white" data-testid="monthly-signups-chart">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Signups Per Month</div>
              <div className="h-52 -ml-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={inviteStats?.monthly || []}>
                    <CartesianGrid stroke="#09090b" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="month" tickFormatter={(m) => (m || "").slice(5)} stroke="#09090b" strokeWidth={2} style={{ fontSize: 11, fontWeight: 700 }} />
                    <YAxis allowDecimals={false} stroke="#09090b" strokeWidth={2} style={{ fontSize: 11, fontWeight: 700 }} />
                    <Tooltip contentStyle={{ border: "2px solid #09090b", borderRadius: 0, boxShadow: "4px 4px 0 0 #09090b" }} />
                    <Bar dataKey="count" fill="#2563eb" stroke="#09090b" strokeWidth={2} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent signups */}
            <div className="mt-4" data-testid="recent-signups">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Recent Signups</div>
              {(inviteStats?.recent || []).length === 0 && (
                <div className="border-2 border-dashed border-zinc-300 p-4 text-center text-sm text-zinc-500">
                  No invite signups yet. Share your link to start recruiting.
                </div>
              )}
              <div className="space-y-2">
                {(inviteStats?.recent || []).map((r) => (
                  <div key={r.id} className="border-2 border-black px-3 py-2 bg-white flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-bold truncate">{r.name}</div>
                      <div className="text-xs text-zinc-500 truncate">{r.email}</div>
                    </div>
                    <div className="text-xs text-zinc-500 font-mono">{r.created_at.slice(0, 10)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="brut bg-white overflow-x-auto">
        <table className="w-full border-collapse" data-testid="users-table">
          <thead>
            <tr className="bg-[#09090b] text-white">
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Name</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Email</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Role</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Mgr Partner</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Field Director</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">CFN Start</th>
              <th className="text-right px-4 py-3 font-bold uppercase text-xs tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map((u) => (
              <tr key={u.id} className="border-b-2 border-black last:border-b-0 hover:bg-[#f4f4f5]">
                <td className="px-4 py-3 font-bold">{u.name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="inline-block border-2 border-black px-2 py-0.5 text-xs font-bold uppercase tracking-widest"
                    style={{
                      background:
                        u.role === "admin" ? "#fb7185"
                        : u.role === "managing_partner" ? "#2563eb"
                        : u.role === "field_director" ? "#c084fc"
                        : "#34d399",
                      color: ["admin", "managing_partner", "field_director"].includes(u.role) ? "#fff" : "#09090b",
                    }}>
                    {u.role === "managing_partner" ? "MP" : u.role === "field_director" ? "FIELD DIR." : u.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-zinc-700">{["agent", "field_director"].includes(u.role) ? (u.managing_partner_id ? mpName(u.managing_partner_id) : <span className="text-zinc-400">—</span>) : "—"}</td>
                <td className="px-4 py-3 text-zinc-700">
                  {u.role === "agent" ? (
                    fieldDirectors.length > 0 && (isAdmin || (isMP && u.managing_partner_id === user.id)) ? (
                      <select
                        value={u.field_director_id || ""}
                        onChange={(e) => reassignFd(u.id, e.target.value)}
                        className="border-2 border-black h-8 px-2 text-xs bg-white"
                        data-testid={`fd-select-${u.id}`}
                      >
                        <option value="">— None —</option>
                        {fieldDirectors
                          .filter((f) => isAdmin || f.managing_partner_id === user.id)
                          .map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    ) : (u.field_director_id ? users.find((x) => x.id === u.field_director_id)?.name || "—" : <span className="text-zinc-400">—</span>)
                  ) : <span className="text-zinc-400">—</span>}
                </td>
                <td className="px-4 py-3">
                  {["agent", "field_director"].includes(u.role) ? (
                    <input
                      type="date"
                      defaultValue={u.cfn_start_date || ""}
                      onBlur={async (e) => {
                        const val = e.target.value;
                        if (val === (u.cfn_start_date || "")) return;
                        try {
                          await api.patch(`/users/${u.id}/profile`, { cfn_start_date: val || null });
                          toast.success("CFN start date saved");
                          load();
                        } catch { toast.error("Could not save"); }
                      }}
                      className="border-2 border-black h-8 px-2 text-xs font-medium bg-white focus:outline-none focus:shadow-[3px_3px_0_0_#09090b]"
                      data-testid={`cfn-start-${u.id}`}
                    />
                  ) : <span className="text-zinc-400">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    {(isAdmin || isMP) && ["agent", "field_director"].includes(u.role) && (isAdmin || u.managing_partner_id === user.id) && u.id !== user.id && (
                      <button
                        onClick={() => toggleRole(u)}
                        className="border-2 border-black px-2 h-8 text-[10px] font-bold uppercase tracking-widest hover:bg-[#c084fc] hover:text-white"
                        data-testid={`toggle-role-${u.id}`}
                        title={u.role === "field_director" ? "Demote to Agent" : "Promote to Field Director"}
                      >
                        {u.role === "field_director" ? "Demote" : "Promote"}
                      </button>
                    )}
                    {isAdmin && ["agent", "field_director"].includes(u.role) && (
                      <button onClick={() => setAssignOpen(u)} className="border-2 border-black p-2 hover:bg-[#2563eb] hover:text-white" data-testid={`assign-mp-${u.id}`} title="Assign Managing Partner">
                        <UserCircleGear size={14} weight="bold" />
                      </button>
                    )}
                    {(isAdmin || (isMP && ["agent", "field_director"].includes(u.role) && u.managing_partner_id === user.id)) && u.id !== user.id && (
                      <button onClick={() => del(u)} className="border-2 border-black p-2 hover:bg-[#fb7185]" data-testid={`delete-user-${u.id}`}>
                        <Trash size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {visibleUsers.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No one here yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Reassign dialog */}
      <Dialog open={!!assignOpen} onOpenChange={(v) => !v && setAssignOpen(null)}>
        <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b]">
          <DialogHeader><DialogTitle className="font-display font-black tracking-tight">Assign Managing Partner</DialogTitle></DialogHeader>
          {assignOpen && (
            <div className="space-y-4">
              <div className="text-sm">Assigning <span className="font-bold">{assignOpen.name}</span> to a Managing Partner:</div>
              <div className="space-y-2">
                <button onClick={() => reassign(assignOpen, null)} className="w-full text-left border-2 border-black px-3 py-2 hover:bg-zinc-100 font-medium" data-testid="assign-unassign">— Unassigned —</button>
                {mps.map((m) => (
                  <button key={m.id} onClick={() => reassign(assignOpen, m.id)}
                    className={`w-full text-left border-2 border-black px-3 py-2 font-medium ${assignOpen.managing_partner_id === m.id ? "bg-[#2563eb]" : "hover:bg-zinc-100"}`}
                    data-testid={`assign-option-${m.id}`}>
                    {m.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
